import api from '../services/api'
import { addQueryString } from '../utils/queryString'

import { LANGUAGES, SURVEY_TYPE } from '@/utils/constants'

const { PORTUGUESE, ENGLISH, SPANISH } = LANGUAGES

const methodToAxiosProperty = {
  GET: 'get',
  POST: 'post',
  PUT: 'put',
  DELETE: 'delete',
}

export default {
  async executeSurveyAPICall(
    { dispatch, state },
    {
      path,
      payload,
      queryParams,
      method = 'GET',
      axiosInstance = api,
      responseType = 'json',
    },
  ) {
    const axiosMethod = methodToAxiosProperty[method.toUpperCase()]

    const parsedPath = queryParams ? addQueryString(path, queryParams) : path

    const isDelete = method.toLowerCase() === methodToAxiosProperty.DELETE

    const methodPayload = isDelete ? { data: payload } : payload

    const requestFailed = {
      [PORTUGUESE]: 'Algo deu errado',
      [ENGLISH]: 'Something went wrong',
      [SPANISH]: 'Algo salió mal',
    }

    try {
      const { data } = await axiosInstance[axiosMethod](parsedPath, methodPayload, {
        responseType,
      })

      return data
    } catch (err) {
      const errorMessage = err?.response?.data?.message || requestFailed[state.language]

      // THIS IS GOING TO BE REPLACED BY A REFRESH TOKEN IN THE FUTURE
      // That is why we have a hard-coded check like this
      const isTokenError = errorMessage === 'Invalid JWT Token'

      if (isTokenError) dispatch('toggleLoginModal', { open: true })
      else dispatch('sendMessage', errorMessage)

      return null
    }
  },

  toggleLoginModal({ commit }, payload) {
    commit('SET_LOGIN_MODAL', payload)
  },

  toggleLanguage({ commit }, payload) {
    commit('SET_LANGUAGE', payload)
    window.localStorage.setItem('language', payload)
  },

  value({ commit }, payload) {
    commit('SET_VALUE', payload)
    window.sessionStorage.setItem('value', payload)
  },

  toggleDarkMode({ commit }, payload) {
    commit('SET_DARK_MODE', payload)
    window.sessionStorage.setItem('darkmode', payload)
  },

  tabBilling({ commit }, payload) {
    commit('SET_TAB_BILLING', payload)
    window.sessionStorage.setItem('tabBilling', payload)
  },

  accType({ commit }, payload) {
    commit('SET_ACC_TYPE', payload)
    window.sessionStorage.setItem('accType', payload)
  },

  rateCard({ commit }, payload) {
    commit('SET_RATE_CARD', payload)
    window.sessionStorage.setItem('rateCard', payload)
  },

  groupName({ commit }, payload) {
    commit('SET_GROUP_NAME', payload)
    window.sessionStorage.setItem('groupName', payload)
  },

  changeFlow({ commit }, payload) {
    commit('SET_CHANGE_FLOW', payload)
    window.sessionStorage.setItem('changeFlow', payload)
  },

  firstAccess({ commit }, payload) {
    commit('SET_FIRST_ACCESS', payload)
    window.sessionStorage.setItem('firstAccess', payload)
  },

  sendMessage({ commit }, payload) {
    commit('SET_MESSAGES', payload)
  },

  modalBtns({ commit }, payload) {
    commit('SET_MODAL_BTNS', payload)
  },

  setGroupAdvancedSettingsModalConfig({ commit }, payload) {
    commit('SET_MODAL_GROUPS_ADVANCED_SETTINGS', payload)
  },

  modalPreview({ commit }, payload) {
    commit('SET_MODAL_PREVIEW', payload)
  },

  modalMedia({ commit }, payload) {
    commit('SET_MODAL_MEDIA', payload)
  },

  modalTemplate({ commit }, payload) {
    commit('SET_MODAL_TEMPLATE', payload)
  },

  modalProperties({ commit }, payload) {
    commit('SET_MODAL_PROPERTIES', payload)
  },

  modalClone({ commit }, payload) {
    commit('SET_MODAL_CLONE', payload)
  },

  toggleSurveyAdvancedSettingsModal({ commit }, payload) {
    commit('SET_TOGGLE_SURVEY_ADVANCED_SETTINGS_MODAL', payload)
  },

  mediaObj({ commit }, payload) {
    commit('SET_MEDIA_OBJ', payload)
  },

  deleteMedia({ commit }, payload) {
    commit('SET_DELETE_MEDIA', payload)
  },

  modalFacialCoding({ commit }, payload) {
    commit('SET_MODAL_FACIAL_CODING', payload)
  },

  facialCodingObj({ commit }, payload) {
    commit('SET_FACIAL_CODING_OBJ', payload)
  },

  userLogin({ commit }, user) {
    commit('SET_TOKEN', user.token)
    commit('SET_USERNAME', user.username)
    commit('SET_USER', user.data)

    window.sessionStorage.setItem('token', user.token)
    window.sessionStorage.setItem('username', user.username)
    window.sessionStorage.setItem('user', JSON.stringify(user.data))
  },

  addNewUser({ commit }, user) {
    commit('ADD_USER', user)
  },

  setUsers({ commit }, users) {
    commit('SET_USERS', users)
  },

  logout({ commit }) {
    commit('SET_TOKEN', '')
    commit('SET_USER', {})
    window.sessionStorage.removeItem('token')
    window.sessionStorage.removeItem('accType')
    window.sessionStorage.removeItem('username')
    window.sessionStorage.removeItem('firstAccess')
    window.sessionStorage.removeItem('groupName')
    window.sessionStorage.removeItem('rateCard')
    window.sessionStorage.removeItem('groupName')
    window.sessionStorage.removeItem('user')
  },

  surveyRegister({ commit }, data) {
    const questionsGroups = data.questions_groups ? data.questions_groups : null

    let surveyQuestionsGroups

    if (questionsGroups) {
      surveyQuestionsGroups = Array.isArray(questionsGroups)
        ? questionsGroups
        : JSON.parse(questionsGroups)
    } else {
      surveyQuestionsGroups = []
    }

    commit('SET_QUESTION_GROUPS', surveyQuestionsGroups)
    commit('SET_HAS_SURVEY', true)

    commit('SET_SURVEY', data)

    window.sessionStorage.setItem('survey', JSON.stringify(data))

    window.sessionStorage.setItem(
      'surveyQuestionsGroups',
      Array.isArray(surveyQuestionsGroups)
        ? JSON.stringify(surveyQuestionsGroups)
        : surveyQuestionsGroups,
    )

    window.sessionStorage.setItem('hasSurvey', true)
  },

  setSurvey({ commit }, payload) {
    commit('SET_SURVEY', payload)
  },

  surveyRemove({ commit }) {
    commit('SET_SURVEY', '')
    commit('SET_HAS_SURVEY', false)

    window.sessionStorage.removeItem('survey')
    window.sessionStorage.removeItem('hasSurvey')
    window.sessionStorage.removeItem('tabBilling')
  },

  setShowConfig({ commit }, payload) {
    commit('SET_SHOW_CONFIG', payload)
  },

  setReportQuestionsDownloadControl({ commit }, payload) {
    commit('SET_REPORT_QUESTIONS_DOWNLOAD_CONTROL', payload)
  },

  // QUOTA ACTIONS
  setStep({ commit }, payload) {
    commit('SET_STEP', payload)
  },

  setWantedCompletes({ commit }, payload) {
    commit('SET_WANTED_COMPLETES', payload)
  },

  setBasicQuestions({ commit }, payload) {
    commit('SET_BASIC_QUESTIONS', payload)
  },

  setAdvancedQuestions({ commit }, payload) {
    commit('SET_ADVANCED_QUESTIONS', payload)
  },

  setAnswers({ commit }, payload) {
    commit('SET_ANSWERS', payload)
  },

  setQuotaJSON({ commit }, payload) {
    commit('SET_QUOTA_JSON', payload)
  },

  setCompleteChecker({ commit }, payload) {
    commit('SET_COMPLETE_CHECKER', payload)
  },

  setQuotaOrder({ commit }, payload) {
    commit('SET_QUOTA_ORDER', payload)
  },

  setCountry({ commit }, payload) {
    commit('SET_COUNTRY', payload)
  },

  setState({ commit }, payload) {
    commit('SET_STATE', payload)
  },

  setCreatedProject({ commit }, payload) {
    commit('SET_CREATED_PROJECT', payload)
  },

  setLoadingSaveSettingsQuota({ commit }, payload) {
    commit('SET_LOADING_SAVE_SETTINGS_QUOTA', payload)
  },

  setLoading({ commit }, payload) {
    commit('SET_LOADING', payload)
  },

  setChartType({ commit }, payload) {
    commit('SET_CHART_TYPE', payload)
  },

  setChartSettings({ commit }, payload) {
    commit('SET_CHART_SETTINGS', payload)
  },

  setSurveyStatusWithChartConfig({ commit }, payload) {
    commit('SET_SURVEY_STATUS_WITH_CHART_CONFIG', payload)
  },

  // SCHEME ACTIONS

  setMatrixDisplay({ commit }, payload) {
    commit('SET_MATRIX_DISPLAY', payload)
  },

  setMatrixFilter({ commit }, payload) {
    commit('SET_MATRIX_FILTER', payload)
  },

  setMatrixFilterReverse({ commit }, payload) {
    commit('SET_MATRIX_FILTER_REVERSE', payload)
  },

  setUpdatedAdvancedOptions({ commit }, payload) {
    commit('SET_UPDATED_ADVANCED_OPTIONS', payload)
  },

  setParams({ commit }, payload) {
    commit('SET_PARAMS', payload)
  },

  setValidSlider({ commit }, payload) {
    commit('SET_VALID_SLIDER', payload)
  },

  setLoadSurvey({ commit }, payload) {
    commit('SET_LOAD_SURVEY', payload)
  },

  setPreview({ commit }, payload) {
    commit('SET_PREVIEW', payload)
  },

  setUpdateQuestions({ commit }, payload) {
    commit('SET_UPDATE_QUESTIONS', payload)
  },

  setGroupNameChange({ commit }, { rename, index }) {
    commit('SET_GROUP_NAME_CHANGE', { rename, index })
  },

  setQuestionGroups({ commit }, payload) {
    commit('SET_QUESTION_GROUPS', payload)
  },

  setCategories({ commit }, payload) {
    commit('SET_CATEGORIES', payload)
  },

  setOthers({ commit }, payload) {
    commit('SET_OTHERS', payload)
  },

  setExclusive({ commit }, payload) {
    commit('SET_EXCLUSIVE', payload)
  },

  setFixRandom({ commit }, payload) {
    commit('SET_FIX_RANDOM', payload)
  },

  setSub({ commit }, payload) {
    commit('SET_SUB', payload)
  },

  setChangeCode({ commit }, payload) {
    commit('SET_CHANGE_CODE', payload)
  },

  setUploaded({ commit }, payload) {
    commit('SET_UPLOADED', payload)
  },

  async updateSurvey({ dispatch, getters }) {
    const response = await dispatch('executeSurveyAPICall', {
      path: 'survey',
      queryParams: { survey_id: getters.survey.id_ },
    })

    dispatch('surveyRegister', response)

    dispatch('setCategories', {
      surveyName: response.name,
      lang: response.lang,
      type: response.survey_type,
      sid: response.id_,
    })

    const editor = JSON.parse(sessionStorage.getItem('editor'))

    if (editor) {
      window.sessionStorage.setItem('editor', false)
    }
  },

  updateSpecificQuestionGroup({ commit }, { index, questions }) {
    commit('UPDATE_SPECIFIC_QUESTION_GROUP', { index, questions })
  },

  updateSurveyGroup({ commit }, groups) {
    commit('UPDATE_SURVEY_GROUP', groups)
  },

  updateQuestionGroup({ commit }, { groupIndex, questionGroup }) {
    commit('UPDATE_QUESTION_GROUP', { groupIndex, questionGroup })
  },

  addAlternatives({ commit }, { questionGroupIndex, questionIndex, type, value }) {
    commit('SET_NEW_ALTERNATIVES', {
      questionGroupIndex,
      questionIndex,
      type,
      value,
    })
  },

  changeSpecificQuestionProperty(
    { commit },
    { questionGroupIndex, questionIndex, property, value },
  ) {
    commit('SET_SPECIFIC_QUESTION_PROPERTY', {
      questionGroupIndex,
      questionIndex,
      property,
      value,
    })
  },

  changeSpecificGroupProperty({ commit }, { questionGroupIndex, property, value }) {
    commit('SET_SPECIFIC_GROUP_PROPERTY', {
      questionGroupIndex,
      property,
      value,
    })
  },

  updateSingleQuestionGroup({ commit }, payload) {
    commit('UPDATE_SINGLE_QUESTION_GROUP', payload)
  },

  pushQuestionGroup({ commit }, payload) {
    commit('PUSH_QUESTION_GROUP', payload)
  },

  preview({ dispatch, getters }, { owQuestions, code }) {
    const surveyType = getters.survey.survey_type

    let questionsTestLink = `${owQuestions}${getters.survey.id_}?questioncode=${code}`

    const surveyTypeIsNotCAWI = [SURVEY_TYPE.CATI, SURVEY_TYPE.CAPI].includes(surveyType)

    if (surveyTypeIsNotCAWI) {
      questionsTestLink = `${questionsTestLink}&interview=y`
    }

    dispatch('modalPreview', {
      url: questionsTestLink,
    })
  },

  changeAdvanced({ commit }, { questionGroupIndex, questionIndex, advancedKey, value }) {
    commit('SET_SPECIFIC_ADVANCED_OPTION', {
      questionGroupIndex,
      questionIndex,
      advancedKey,
      value,
    })
  },

  changeGroupAdvancedSettings({ commit }, { questionGroupIndex, advancedKey, value }) {
    commit('SET_SPECIFIC_GROUP_ADVANCED_OPTION', {
      questionGroupIndex,
      advancedKey,
      value,
    })
  },

  changeSurveyAdvancedOptions({ commit }, { advancedKey, value }) {
    commit('SET_SPECIFIC_SURVEY_ADVANCED_OPTION', {
      advancedKey,
      value,
    })
  },

  setUpdateLog({ commit }, payload) {
    commit('SET_UPDATE_LOG', payload)
  },

  changeQuestionLogic({ commit }, { questionGroupIndex, questionIndex, logic }) {
    commit('SET_SPECIFIC_LOGIC', { questionGroupIndex, questionIndex, logic })
  },

  setQuestionCodeValidation({ commit }, payload) {
    commit('SET_QUESTION_CODE_VALIDATION', payload)
  },

  setIsVisitorAccess({ commit }, payload) {
    commit('SET_IS_VISITOR_ACCESS', payload)
  },
}
